import { Injectable, InjectionToken, inject } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect } from '@ngrx/effects';

import { asyncScheduler, Observable, of, race, SchedulerLike } from 'rxjs';
import { catchError, delay, filter, map, mergeMap } from 'rxjs/operators';
import {
  EntityAction,
  EntityActionFactory,
  EntityDataService,
  EntityOp,
  makeSuccessOp,
  ofEntityOp,
  PersistenceResultHandler,
} from '@ngrx/data';
import { CcoDefaultDataService } from '@app/data/cco-default-data-service';

const ENTITY_EFFECTS_SCHEDULER = new InjectionToken<SchedulerLike>(
  '@ngrx/data Entity Effects Scheduler'
);

export const multiPersistOps: EntityOp[] = [
  EntityOp.SAVE_UPSERT_MANY,
];

@Injectable()
export class MultiEntityEffects {
  private actions = inject<Actions<EntityAction>>(Actions);
  private dataService = inject(EntityDataService);
  private entityActionFactory = inject(EntityActionFactory);
  private resultHandler = inject(PersistenceResultHandler);
  private scheduler = inject<SchedulerLike>(ENTITY_EFFECTS_SCHEDULER, { optional: true });

  // See https://github.com/ReactiveX/rxjs/blob/master/doc/marble-testing.md
  /** Delay for error and skip observables. Must be multiple of 10 for marble testing. */
  private responseDelay = 10;

  /**
   * Observable of non-null cancellation correlation ids from CANCEL_PERSIST actions
   */
  cancel$: Observable<any> = createEffect(
    () =>
      { return this.actions.pipe(
        ofEntityOp(EntityOp.CANCEL_PERSIST),
        map((action: EntityAction) => action.payload.correlationId),
        filter(id => id != null)
      ); },
    { dispatch: false }
  );

  // `mergeMap` allows for concurrent requests which may return in any order
  persist$: Observable<Action> = createEffect(() =>
    { return this.actions.pipe(
      ofEntityOp(multiPersistOps),
      mergeMap(action => this.persist(action))
    ); }
  );

  /**
   * Perform the requested persistence operation and return a scalar Observable<Action>
   * that the effect should dispatch to the store after the server responds.
   * @param action A persistence operation EntityAction
   */
  persist(action: EntityAction): Observable<Action> {
    if (action.payload.skip) {
      // Should not persist. Pretend it succeeded.
      return this.handleSkipSuccess$(action);
    }
    if (action.payload.error) {
      return this.handleError$(action)(action.payload.error);
    }
    try {
      // Cancellation: returns Observable of CANCELED_PERSIST for a persistence EntityAction
      // whose correlationId matches cancellation correlationId
      const c = this.cancel$.pipe(
        filter(id => action.payload.correlationId === id),
        map(() =>
          this.entityActionFactory.createFromAction(action, {
            entityOp: EntityOp.CANCELED_PERSIST,
          })
        )
      );

      // Data: entity collection DataService result as a successful persistence EntityAction
      const d = this.callDataService(action).pipe(
        map(this.resultHandler.handleSuccess(action)),
        catchError(this.handleError$(action))
      );

      // Emit which ever gets there first; the other observable is terminated.
      return race(c, d);
    } catch (err: any) {
      return this.handleError$(action)(err);
    }
  }

  private callDataService(action: EntityAction) {
    const { entityName, entityOp, data } = action.payload;
    const service = this.dataService.getService(entityName);
    switch (entityOp) {
      case EntityOp.SAVE_UPSERT_MANY:
        return (service as CcoDefaultDataService<any>).upsertMany(data).pipe(
          map((upsertedEntities: any) => {
            const hasData = upsertedEntities && upsertedEntities.length > 0;
            return hasData ? upsertedEntities : data;
          })
        );
      default:
        throw new Error(`Persistence action "${entityOp}" is not implemented.`);
    }
  }

  /**
   * Handle error result of persistence operation on an EntityAction,
   * returning a scalar observable of error action
   */
  private handleError$(
    action: EntityAction
  ): (error: Error) => Observable<EntityAction> {
    // Although error may return immediately,
    // ensure observable takes some time,
    // as app likely assumes asynchronous response.
    return (error: Error) =>
      of(this.resultHandler.handleError(action)(error)).pipe(
        delay(this.responseDelay, this.scheduler || asyncScheduler)
      );
  }

  /**
   * Because EntityAction.payload.skip is true, skip the persistence step and
   * return a scalar success action that looks like the operation succeeded.
   */
  private handleSkipSuccess$(
    originalAction: EntityAction
  ): Observable<EntityAction> {
    const successOp = makeSuccessOp(originalAction.payload.entityOp);
    const successAction = this.entityActionFactory.createFromAction(
      originalAction,
      {
        entityOp: successOp,
      }
    );
    // Although returns immediately,
    // ensure observable takes one tick (by using a promise),
    // as app likely assumes asynchronous response.
    return of(successAction).pipe(
      delay(this.responseDelay, this.scheduler || asyncScheduler)
    );
  }
}
